import { Controller } from "@hotwired/stimulus";

// Business Rules — Dish/Holiday/Occasion Form UX (Stimulus)
// - Dish: Either select an existing shared dish OR enter a new shared dish name.
//   Optional creation-note text can accompany new shared dishes.
//   When selecting an existing dish, disable and clear new dish fields.
// - Holiday: Optional; users may select an existing holiday.
// - Occasion: May select existing tags and optionally create ONE new occasion.
//   If creating, enforce name and mutually exclusive date logic client-side.

const DISH_SEARCH_MIN_QUERY_LENGTH = 2;
const NEW_DISH_MIN_QUERY_LENGTH = 4;
const DISH_SEARCH_DEBOUNCE_MS = 180;

export default class extends Controller {
  static values = {
    dishAutocompleteUrl: String,
    previousPreparationCopyUrl: String,
    previousPreparationCopyApplyUrl: String,
    autoFillPreparationName: Boolean,
    allowNewDishCreation: Boolean,
    historyDishIds: Array,
    errorSummaryMode: String,
    noticeMessage: String
  };

  static targets = [
    "form",
    "errorBox",

    "dishSelect",
    "dishSearchWrap",
    "dishSearchInput",
    "newDishField",
    "dishExistingSelection",
    "dishExistingName",
    "dishNewSelection",
    "dishNewName",
    "dishNewNameInline",
    "newDishDetails",
    "preparationNameField",
    "preparationNameError",
    "newDishDescription",
    "whatChangedField",
    "dishSuggestions",
    "dishSuggestionsList",
    "dishSuggestionsStatus",
    "dishGroupError",
    "dishGroupFieldset",
    "previousPreparationCopyPanel",
    "previousPreparationCopyChoices",
    "previousPreparationCopyStatus",
    "startingPointStatus",
    "occasionGroupFieldset",
    "occasionGroupError",
    "newOccasionCheckbox",
    "newOccasionFields",
    "occasionTypeSelect",
    "dateCooked",
    "dateCookedError",
    "ingredientsContainer",
    "ingredientTemplate",
    "stepsContainer",
    "stepTemplate",
    "recipeSectionsContainer",
    "recipeSectionTemplate",
    "travelFields"
  ];

  connect() {
    this.dishSuggestionFetchTimer = null;
    this.currentDishAutocompleteRequest = null;
    this.currentRestoredDishRequest = null;
    this.currentPreviousPreparationCopyRequest = null;
    this.previousPreparationCopyRequestedDishId = null;
    this.currentPreviousPreparationCopyApplyRequest = null;
    this.dishSuggestionOptionButtons = [];
    this.activeDishSuggestionIndex = -1;
    this.dishSuggestionsDismissedQuery = null;
    this.preparationNameTouched = !this.autoFillPreparationNameValue;
    if (!this.preparationNameTouched && this.hasPreparationNameFieldTarget) {
      const existingPreparationName = this.normalizedDishName(this.preparationNameFieldTarget.value);
      const selectedDishName = this.normalizedDishName(
        this.selectedDishName() || (this.hasNewDishFieldTarget ? this.newDishFieldTarget.value : "")
      );

      if (existingPreparationName && (!selectedDishName || existingPreparationName !== selectedDishName)) {
        this.preparationNameTouched = true;
      }
    }
    this.hasAttemptedSubmit = false;
    this.serverRenderedNoticeElement = null;
    this.touchedValidationKeys = new Set();
    this.dirtyValidationKeys = new Set();
    this.touchedValidationFields = new Set();
    this.dirtyValidationFields = new Set();

    this.initializeDishSelectionState();
    if (this.hasNewOccasionCheckboxTarget && this.hasNewOccasionFieldsTarget) {
      this.toggleNewOccasion({ target: this.newOccasionCheckboxTarget });
    }
    this.boundHandleTurboBeforeCache = this.handleTurboBeforeCache.bind(this);
    document.addEventListener("turbo:before-cache", this.boundHandleTurboBeforeCache);
    this.initializeAccordionSections();
    this.updateOccasionFieldRequirements();
  }

  disconnect() {
    this.abortDishAutocompleteRequest();
    this.abortRestoredDishRequest();
    this.abortPreviousPreparationCopyRequest();
    this.abortPreviousPreparationCopyApplyRequest();
    this.clearDishSuggestionTimers();
    if (this.boundHandleTurboBeforeCache) {
      document.removeEventListener("turbo:before-cache", this.boundHandleTurboBeforeCache);
    }
  }

  handleWindowClick(event) {
    if (!this.hasDishSuggestionsTarget) return;
    if (this.hasDishSearchWrapTarget && this.dishSearchWrapTarget.contains(event.target)) return;

    this.dismissDishSuggestions();
  }

  toggleAccordion(event) {
    event.preventDefault();

    const section = event.currentTarget.closest("[data-accordion-section]");
    if (!section) return;

    this.setAccordionSectionOpen(section, !this.accordionSectionOpen(section));
  }

  handleTurboBeforeCache() {
    this.initializeAccordionSections();
  }

  initializeAccordionSections() {
    const sections = this.element.querySelectorAll("[data-accordion-section]");
    sections.forEach((section) => {
      this.setAccordionSectionOpen(section, this.accordionSectionDefaultOpen(section));
    });
  }

  accordionSectionOpen(section) {
    return section.hasAttribute("open");
  }

  accordionSectionDefaultOpen(section) {
    return section.dataset.accordionDefaultOpen === "true";
  }

  setAccordionSectionOpen(section, isOpen) {
    if (isOpen) {
      section.setAttribute("open", "");
    } else {
      section.removeAttribute("open");
    }

    const trigger = section.querySelector(":scope > [data-accordion-trigger]");
    if (trigger) {
      trigger.setAttribute("aria-expanded", String(isOpen));
    }

    Array.from(section.children).forEach((child) => {
      if (child === trigger) return;
      child.classList.toggle("hidden", !isOpen);
    });
  }

  dishSearchInput(event) {
    const query = this.normalizedDishName(event.target.value);
    this.dishSuggestionsDismissedQuery = null;
    this.markValidationDirty("dish");

    this.maybeFillPreparationName(query);
    this.markInvalid(this.dishSearchInputTarget, false);
    this.clearDishGroupError();
    this.removeSummary("dish");

    if (!query) {
      this.abortDishAutocompleteRequest();
      this.clearDishSuggestionTimers();
      this.clearDishSuggestions();
      return;
    }

    if (query.length < DISH_SEARCH_MIN_QUERY_LENGTH) {
      this.abortDishAutocompleteRequest();
      this.clearDishSuggestionTimers();
      this.clearDishSuggestions();
      return;
    }

    this.clearDishSuggestionTimers();
    this.dishSuggestionFetchTimer = window.setTimeout(() => {
      this.dishSuggestionFetchTimer = null;
      this.fetchDishSuggestions(query);
    }, DISH_SEARCH_DEBOUNCE_MS);
  }

  dishSearchFocused() {
    const query = this.normalizedDishName(this.dishSearchInputTarget.value);
    if (!query || query.length < DISH_SEARCH_MIN_QUERY_LENGTH) return;

    this.dishSuggestionsDismissedQuery = null;
    this.clearDishSuggestionTimers();
    this.fetchDishSuggestions(query);
  }

  dishSearchKeydown(event) {
    if (!this.hasDishSearchInputTarget) return;
    const query = this.normalizedDishName(this.dishSearchInputTarget.value);

    if (event.key === "Escape") {
      const suggestionsOpen = this.hasDishSuggestionsTarget && !this.dishSuggestionsTarget.classList.contains("hidden");
      const autocompletePending = Boolean(this.dishSuggestionFetchTimer || this.currentDishAutocompleteRequest);
      if (!suggestionsOpen && !autocompletePending) return;

      event.preventDefault();
      event.stopPropagation();
      this.dismissDishSuggestions(query);
      return;
    }

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (this.dishSuggestionOptionButtons.length === 0 && query.length >= DISH_SEARCH_MIN_QUERY_LENGTH) {
        this.fetchDishSuggestions(query);
        return;
      }

      this.moveDishSuggestionActiveIndex(event.key === "ArrowDown" ? 1 : -1);
      return;
    }

    if (event.key !== "Enter") return;

    if (query.length < DISH_SEARCH_MIN_QUERY_LENGTH) return;

    event.preventDefault();

    if (this.activeDishSuggestionIndex >= 0 && this.dishSuggestionOptionButtons[this.activeDishSuggestionIndex]) {
      this.dishSuggestionOptionButtons[this.activeDishSuggestionIndex].click();
      return;
    }
    if (this.dishSuggestionOptionButtons.length === 0) {
      this.fetchDishSuggestions(query);
    }
  }

  selectDishOption(event) {
    event.preventDefault();

    const option = event.currentTarget;
    const optionType = option?.dataset?.dishOptionType;
    const optionName = this.normalizedDishName(option?.dataset?.dishOptionName);

    if (optionType === "existing") {
      this.chooseExistingDish(option.dataset.dishOptionId, optionName);
    } else if (optionType === "new") {
      this.chooseNewDish(optionName);
    }
  }

  changeDishSelection(event) {
    event.preventDefault();
    this.abortPreviousPreparationCopyApplyRequest();
    this.clearPreviousPreparationCopyAnnouncement();
    this.enablePreviousPreparationCopyAfterDishChange();
    this.markValidationDirty("dish");

    this.clearDishSuggestionTimers();
    this.clearDishSuggestions();

    const prefill = this.selectedDishName() || (this.hasNewDishFieldTarget ? this.normalizedDishName(this.newDishFieldTarget.value) : "");
    if (this.hasDishSelectTarget) {
      this.dishSelectTarget.value = "";
      this.dishSelectTarget.dataset.dishName = "";
    }
    if (this.hasNewDishFieldTarget) this.newDishFieldTarget.value = "";
    if (this.hasNewDishDescriptionTarget) this.newDishDescriptionTarget.value = "";

    this.showDishSearchMode(prefill);
    this.clearDishGroupError();
    this.removeSummary("dish");

    if (prefill) {
      this.fetchDishSuggestions(prefill);
    }
    const draftField = this.hasDishSelectTarget ? this.dishSelectTarget : (this.hasNewDishFieldTarget ? this.newDishFieldTarget : null);
    this.dispatchDraftFieldChange(draftField);
    this.dishSearchInputTarget.focus();
  }

  markPreparationNameTouched() {
    this.preparationNameTouched = true;
    this.markValidationTouched("preparation_name");
    this.markValidationDirty("preparation_name");
  }

  handleFieldInput(event) {
    this.trackValidationInteraction(event?.target, { dirty: true });
    if (this.hasDishSearchInputTarget && event?.target === this.dishSearchInputTarget && this.selectedExistingDishId() !== "") {
      this.initializeDishSelectionState();
    }
    this.revalidateForRecovery(event?.target, { source: "input" });
    this.refreshSortableListForField(event?.target);
  }

  handleFieldChange(event) {
    this.trackValidationInteraction(event?.target, { dirty: true });
    this.syncDishSelectionFromRestoredField(event?.target);
    if (this.hasDishSelectTarget && event?.target === this.dishSelectTarget && this.dishSelectTarget.value !== "" && !this.dishSelectTarget.dataset.dishName) {
      window.setTimeout(() => {
        if (this.dishSelectTarget.value !== "") this.initializeDishSelectionState();
      }, 0);
    }
    this.revalidateForRecovery(event?.target, { source: "change" });
    this.refreshSortableListForField(event?.target);
  }

  trackValidationInteraction(element, options = {}) {
    const validationKey = this.validationKeyForElement(element);
    if (!this.isTrackedValidationKey(validationKey)) return;

    const dirty = options.dirty === true;
    this.markValidationTouched(validationKey);
    if (dirty) this.markValidationDirty(validationKey);

    const fieldKey = this.validationFieldKey(element);
    if (!fieldKey) return;

    this.touchedValidationFields.add(fieldKey);
    if (dirty) this.dirtyValidationFields.add(fieldKey);
  }

  revalidateForRecovery(element, options = {}) {
    if (!this.hasAttemptedSubmit) return;

    const validationKey = this.validationKeyForElement(element);
    if (validationKey === "date_cooked") {
      this.validateDateCooked({ force: true });
      return;
    }

    if (validationKey === "preparation_name") {
      this.validatePreparationName({ force: true });
      return;
    }

    if (validationKey === "occasion") {
      this.validateOccasionGroup({ force: true, skipFocusBoundaryCheck: true });
      return;
    }

    if (validationKey !== "dish") return;

    const source = options.source || "";
    const dishQuery = this.hasDishSearchInputTarget ? this.normalizedDishName(this.dishSearchInputTarget.value) : "";
    const searching = source === "input" && element === this.dishSearchInputTarget && dishQuery.length > 0;

    if (searching) return;

    this.validateDishGroup({ force: true, skipFocusBoundaryCheck: true });
  }

  validationKeyForElement(element) {
    if (!element) return null;
    if (this.hasDishGroupFieldsetTarget && this.dishGroupFieldsetTarget.contains(element)) return "dish";
    if (this.hasDateCookedTarget && element === this.dateCookedTarget) return "date_cooked";
    if (this.hasPreparationNameFieldTarget && element === this.preparationNameFieldTarget) return "preparation_name";
    if (this.hasOccasionGroupFieldsetTarget && this.occasionGroupFieldsetTarget.contains(element)) return "occasion";

    return null;
  }

  isTrackedValidationKey(key) {
    return key === "dish" || key === "date_cooked" || key === "preparation_name" || key === "occasion";
  }

  markValidationTouched(key) {
    if (!this.isTrackedValidationKey(key)) return;
    this.touchedValidationKeys.add(key);
  }

  markValidationDirty(key) {
    if (!this.isTrackedValidationKey(key)) return;
    this.touchedValidationKeys.add(key);
    this.dirtyValidationKeys.add(key);
  }

  shouldValidate(key, options = {}) {
    if (!this.isTrackedValidationKey(key)) return false;
    if (options.force === true) return true;
    if (this.hasAttemptedSubmit) return true;

    return this.touchedValidationKeys.has(key) || this.dirtyValidationKeys.has(key);
  }

  validationFieldKey(element) {
    if (!element) return null;

    return element.name || element.id || null;
  }

  fieldInteracted(element) {
    const fieldKey = this.validationFieldKey(element);
    if (!fieldKey) return false;

    return this.touchedValidationFields.has(fieldKey) || this.dirtyValidationFields.has(fieldKey);
  }

  fetchDishSuggestions(query) {
    if (!query) return;
    if (query.length < DISH_SEARCH_MIN_QUERY_LENGTH) {
      this.clearDishSuggestions();
      return;
    }
    if (!this.hasDishAutocompleteUrlValue) {
      this.renderDishSuggestions([], query);
      return;
    }

    this.abortDishAutocompleteRequest();
    const request = new AbortController();
    this.currentDishAutocompleteRequest = request;

    const endpoint = new URL(this.dishAutocompleteUrlValue, window.location.origin);
    endpoint.searchParams.set("q", query);

    fetch(endpoint.toString(), {
      headers: { Accept: "application/json" },
      signal: request.signal
    })
      .then(response => (response.ok ? response.json() : []))
      .then(results => {
        if (!this.hasDishSearchInputTarget) return;
        if (this.normalizedDishName(this.dishSearchInputTarget.value) !== query) return;
        if (this.dishSuggestionsDismissedQuery === query) return;
        this.renderDishSuggestions(Array.isArray(results) ? results : [], query);
      })
      .catch(error => {
        if (error?.name === "AbortError") return;
        this.clearDishSuggestions();
      })
      .finally(() => {
        if (this.currentDishAutocompleteRequest === request) {
          this.currentDishAutocompleteRequest = null;
        }
      });
  }

  renderDishSuggestions(results, query) {
    if (!this.hasDishSuggestionsTarget || !this.hasDishSuggestionsListTarget || !this.hasDishSuggestionsStatusTarget) return;

    this.dishSuggestionsListTarget.innerHTML = "";
    this.dishSuggestionOptionButtons = [];
    this.activeDishSuggestionIndex = -1;
    this.dishSuggestionsTarget.classList.toggle(
      "ec-dish-combobox-panel--validation",
      this.hasDishGroupErrorTarget && !this.dishGroupErrorTarget.classList.contains("hidden")
    );

    const normalizedQuery = this.normalizedDishName(query);
    if (!normalizedQuery || normalizedQuery.length < DISH_SEARCH_MIN_QUERY_LENGTH) {
      this.clearDishSuggestions();
      return;
    }

    const existingMatches = Array.isArray(results)
      ? results
        .filter((dish) => dish?.id && dish?.name)
        .map((dish) => ({
          type: "existing",
          id: dish.id.toString(),
          name: this.normalizedDishName(dish.name)
        }))
        .slice(0, 6)
      : [];
    const canCreateNewDish = this.canCreateNewDish(normalizedQuery);
    if (existingMatches.length === 0 && !canCreateNewDish) {
      this.clearDishSuggestions();
      return;
    }

    if (existingMatches.length > 0) {
      this.dishSuggestionsStatusTarget.textContent = "Existing dish";
      this.dishSuggestionsStatusTarget.classList.remove("hidden");
    } else {
      this.dishSuggestionsStatusTarget.textContent = "";
      this.dishSuggestionsStatusTarget.classList.add("hidden");
    }

    let optionIndex = 0;

    const appendDishOption = (option, sectionClass = "") => {
      const li = document.createElement("li");
      if (sectionClass) li.className = sectionClass;
      const button = this.createDishSuggestionButton(option, optionIndex);
      optionIndex += 1;
      li.appendChild(button);
      this.dishSuggestionsListTarget.appendChild(li);
      this.dishSuggestionOptionButtons.push(button);
    };

    existingMatches.forEach((option) => {
      appendDishOption(option);
    });

    if (canCreateNewDish) {
      const createOption = {
        type: "new",
        name: normalizedQuery,
        standalone: existingMatches.length === 0,
        label: `Create new dish: “${normalizedQuery}”`
      };

      if (existingMatches.length > 0) {
        const li = document.createElement("li");
        li.className = "ec-dish-combobox-create-section";

        const helper = document.createElement("p");
        helper.className = "ec-dish-combobox-create-help";
        helper.textContent = "Or create a new dish";

        const button = this.createDishSuggestionButton(createOption, optionIndex);
        li.append(helper, button);
        this.dishSuggestionsListTarget.appendChild(li);
        this.dishSuggestionOptionButtons.push(button);
      } else {
        appendDishOption(createOption, "ec-dish-combobox-create-section ec-dish-combobox-create-section--standalone");
      }
    }

    this.dishSuggestionsTarget.classList.remove("hidden");
    this.dishSuggestionsTarget.closest(".ec-dish-combobox-shell")?.classList.add("ec-dish-combobox-shell--open");
    this.dishSearchInputTarget.setAttribute("aria-expanded", "true");
    if (this.dishSuggestionOptionButtons.length > 0) {
      this.setDishSuggestionActiveIndex(0);
    }
  }

  createDishSuggestionButton(option, index) {
    const button = document.createElement("button");
    button.type = "button";
    button.id = `preparation_dish_search_option_${index}`;
    button.className = "ec-dish-combobox-option";
    button.dataset.action = "click->dish-holiday-form#selectDishOption";
    button.dataset.dishOptionType = option.type;
    button.dataset.dishOptionName = option.name;
    button.dataset.dishSuggestionIndex = index.toString();
    button.setAttribute("role", "option");
    button.setAttribute("aria-selected", "false");

    if (option.type === "existing") {
      button.dataset.dishOptionId = option.id;
      button.classList.add("ec-dish-combobox-option--existing");
      button.textContent = option.name;
      return button;
    }

    button.classList.add("ec-dish-combobox-option--create");
    button.classList.add(option.standalone ? "ec-dish-combobox-option--create-standalone" : "ec-dish-combobox-option--create-secondary");
    button.textContent = option.label || `Create new dish: “${option.name}”`;
    return button;
  }

  setDishSuggestionActiveIndex(nextIndex) {
    if (this.dishSuggestionOptionButtons.length === 0) {
      this.activeDishSuggestionIndex = -1;
      if (this.hasDishSearchInputTarget) {
        this.dishSearchInputTarget.removeAttribute("aria-activedescendant");
      }
      return;
    }

    const normalizedIndex = ((nextIndex % this.dishSuggestionOptionButtons.length) + this.dishSuggestionOptionButtons.length) % this.dishSuggestionOptionButtons.length;
    this.activeDishSuggestionIndex = normalizedIndex;
    const activeButton = this.dishSuggestionOptionButtons[normalizedIndex];

    this.dishSuggestionOptionButtons.forEach((button, index) => {
      const active = index === normalizedIndex;
      button.classList.toggle("is-active", active);
      button.setAttribute("aria-selected", active ? "true" : "false");
      if (active) button.scrollIntoView({ block: "nearest" });
    });

    if (this.hasDishSearchInputTarget && activeButton) {
      this.dishSearchInputTarget.setAttribute("aria-activedescendant", activeButton.id);
    }
  }

  moveDishSuggestionActiveIndex(delta) {
    if (this.dishSuggestionOptionButtons.length === 0) return;

    const startIndex = this.activeDishSuggestionIndex >= 0 ? this.activeDishSuggestionIndex : 0;
    this.setDishSuggestionActiveIndex(startIndex + delta);
  }

  chooseExistingDish(dishId, dishName, options = {}) {
    const normalizedDishName = this.normalizedDishName(dishName);
    if (!dishId || !this.hasDishSelectTarget) return;
    this.abortPreviousPreparationCopyApplyRequest();
    this.clearPreviousPreparationCopyAnnouncement();
    this.markValidationDirty("dish");

    const optionId = dishId.toString();
    if (this.dishSelectTarget.tagName === "SELECT") {
      let option = Array.from(this.dishSelectTarget.options).find((candidate) => candidate.value === optionId);

      if (!option && normalizedDishName) {
        option = document.createElement("option");
        option.value = optionId;
        option.textContent = normalizedDishName;
        this.dishSelectTarget.appendChild(option);
      }
    }

    this.dishSelectTarget.value = optionId;
    this.dishSelectTarget.dataset.dishName = normalizedDishName || this.selectedDishName();
    if (this.hasNewDishFieldTarget) this.newDishFieldTarget.value = "";
    if (this.hasNewDishDescriptionTarget) this.newDishDescriptionTarget.value = "";

    this.showExistingDishSelection(normalizedDishName || this.selectedDishName());
    if (!options.preservePreparationName) {
      this.maybeFillPreparationName(normalizedDishName || this.selectedDishName());
    }
    this.clearDishSuggestions();
    this.clearDishGroupError();
    this.removeSummary("dish");

    if (options.dispatchChange !== false) {
      this.dispatchDraftFieldChange(this.dishSelectTarget);
    }

    this.dispatchPreparationEvent("preparation:dish-selected");
  }

  chooseRecipeDish(event) {
    const dish = event?.detail;
    if (!dish?.id) return;

    this.chooseExistingDish(dish.id, dish.name);
  }

  chooseNewDish(rawDishName, preserveNote = false, options = {}) {
    const normalizedDishName = this.normalizedDishName(rawDishName);
    if (!normalizedDishName) return;
    this.abortPreviousPreparationCopyApplyRequest();
    this.clearPreviousPreparationCopyAnnouncement();
    this.markValidationDirty("dish");

    if (this.hasDishSelectTarget) {
      this.dishSelectTarget.value = "";
      this.dishSelectTarget.dataset.dishName = "";
    }
    if (this.hasNewDishFieldTarget) this.newDishFieldTarget.value = normalizedDishName;

    this.showNewDishSelection(normalizedDishName, preserveNote);
    this.maybeFillPreparationName(normalizedDishName);
    this.clearDishSuggestions();
    this.clearDishGroupError();
    this.removeSummary("dish");

    if (options.dispatchChange !== false) {
      this.dispatchDraftFieldChange(this.newDishFieldTarget);
    }

    this.dispatchPreparationEvent("preparation:dish-selected");
  }

  initializeDishSelectionState() {
    const selectedExistingDishName = this.selectedDishName();
    const selectedNewDishName = this.hasNewDishFieldTarget ? this.normalizedDishName(this.newDishFieldTarget.value) : "";

    if (selectedNewDishName) {
      this.chooseNewDish(selectedNewDishName, true, { dispatchChange: false });
      this.syncPreviousPreparationCopyPanel();
      return;
    }

    if (selectedExistingDishName) {
      this.showExistingDishSelection(selectedExistingDishName);
      this.maybeFillPreparationName(selectedExistingDishName);
      this.syncPreviousPreparationCopyPanel();
      return;
    }

    if (this.selectedExistingDishId() !== "") {
      this.showDishSearchMode();
      this.fetchRestoredDishName(this.selectedExistingDishId());
      this.syncPreviousPreparationCopyPanel();
      return;
    }

    this.showDishSearchMode();
    this.syncPreviousPreparationCopyPanel();
  }

  syncDishSelectionFromRestoredField(field) {
    if (!field) return false;
    if (field !== this.dishSelectTarget && (!this.hasNewDishFieldTarget || field !== this.newDishFieldTarget)) return false;

    this.initializeDishSelectionState();
    return true;
  }

  dispatchDraftFieldChange(field) {
    if (!field) return;

    field.dispatchEvent(new Event("change", { bubbles: true }));
  }

  dispatchPreparationEvent(name) {
    this.element.dispatchEvent(new CustomEvent(name, { bubbles: true }));
  }

  showDishSearchMode(prefill = "") {
    if (this.hasDishSearchWrapTarget) this.dishSearchWrapTarget.classList.remove("hidden");
    if (this.hasDishExistingSelectionTarget) this.dishExistingSelectionTarget.classList.add("hidden");
    if (this.hasDishNewSelectionTarget) this.dishNewSelectionTarget.classList.add("hidden");
    if (this.hasNewDishDetailsTarget) this.newDishDetailsTarget.classList.add("hidden");
    if (this.hasNewDishDescriptionTarget) this.newDishDescriptionTarget.disabled = true;
    if (this.hasDishSearchInputTarget) {
      this.dishSearchInputTarget.value = prefill;
      this.dishSearchInputTarget.setAttribute("aria-expanded", "false");
      this.dishSearchInputTarget.removeAttribute("aria-activedescendant");
    }
    this.updateWhatChangedFieldVisibility();
  }

  showExistingDishSelection(dishName) {
    const normalizedDishName = this.normalizedDishName(dishName);

    if (this.hasDishSearchWrapTarget) this.dishSearchWrapTarget.classList.add("hidden");
    if (this.hasDishExistingSelectionTarget) this.dishExistingSelectionTarget.classList.remove("hidden");
    if (this.hasDishNewSelectionTarget) this.dishNewSelectionTarget.classList.add("hidden");
    if (this.hasNewDishDetailsTarget) this.newDishDetailsTarget.classList.add("hidden");
    if (this.hasDishExistingNameTarget) this.dishExistingNameTarget.textContent = normalizedDishName;
    if (this.hasDishSearchInputTarget) {
      this.dishSearchInputTarget.value = normalizedDishName;
      this.dishSearchInputTarget.setAttribute("aria-expanded", "false");
      this.dishSearchInputTarget.removeAttribute("aria-activedescendant");
    }
    if (this.hasNewDishFieldTarget) this.newDishFieldTarget.value = "";
    if (this.hasNewDishDescriptionTarget) {
      this.newDishDescriptionTarget.value = "";
      this.newDishDescriptionTarget.disabled = true;
    }
    this.updateWhatChangedFieldVisibility();
  }

  showNewDishSelection(dishName, preserveNote = false) {
    const normalizedDishName = this.normalizedDishName(dishName);

    if (this.hasDishSearchWrapTarget) this.dishSearchWrapTarget.classList.add("hidden");
    if (this.hasDishExistingSelectionTarget) this.dishExistingSelectionTarget.classList.add("hidden");
    if (this.hasDishNewSelectionTarget) this.dishNewSelectionTarget.classList.remove("hidden");
    if (this.hasNewDishDetailsTarget) this.newDishDetailsTarget.classList.remove("hidden");
    if (this.hasDishNewNameTarget) this.dishNewNameTarget.textContent = normalizedDishName;
    if (this.hasDishNewNameInlineTarget) this.dishNewNameInlineTarget.textContent = normalizedDishName;
    if (this.hasDishSearchInputTarget) {
      this.dishSearchInputTarget.value = normalizedDishName;
      this.dishSearchInputTarget.setAttribute("aria-expanded", "false");
      this.dishSearchInputTarget.removeAttribute("aria-activedescendant");
    }
    if (this.hasNewDishFieldTarget) this.newDishFieldTarget.value = normalizedDishName;
    if (this.hasNewDishDescriptionTarget) {
      if (!preserveNote) this.newDishDescriptionTarget.value = "";
      this.newDishDescriptionTarget.disabled = false;
    }
    this.updateWhatChangedFieldVisibility();
  }

  clearDishSuggestions() {
    if (!this.hasDishSuggestionsTarget || !this.hasDishSuggestionsListTarget || !this.hasDishSuggestionsStatusTarget) return;

    this.dishSuggestionsStatusTarget.textContent = "";
    this.dishSuggestionsListTarget.innerHTML = "";
    this.dishSuggestionsTarget.classList.add("hidden");
    this.dishSuggestionsTarget.classList.remove("ec-dish-combobox-panel--validation");
    this.dishSuggestionsTarget.closest(".ec-dish-combobox-shell")?.classList.remove("ec-dish-combobox-shell--open");
    this.dishSuggestionOptionButtons = [];
    this.activeDishSuggestionIndex = -1;
    if (this.hasDishSearchInputTarget) {
      this.dishSearchInputTarget.setAttribute("aria-expanded", "false");
      this.dishSearchInputTarget.removeAttribute("aria-activedescendant");
    }
  }

  dismissDishSuggestions(query = null) {
    const dismissedQuery = query ?? (this.hasDishSearchInputTarget ? this.normalizedDishName(this.dishSearchInputTarget.value) : "");

    this.dishSuggestionsDismissedQuery = dismissedQuery;
    this.abortDishAutocompleteRequest();
    this.clearDishSuggestionTimers();
    this.clearDishSuggestions();
  }

  abortDishAutocompleteRequest() {
    if (!this.currentDishAutocompleteRequest) return;

    this.currentDishAutocompleteRequest.abort();
    this.currentDishAutocompleteRequest = null;
  }

  fetchRestoredDishName(dishId) {
    if (!dishId || !this.hasDishAutocompleteUrlValue) return;

    if (this.currentRestoredDishRequest) this.currentRestoredDishRequest.abort();

    const request = new AbortController();
    this.currentRestoredDishRequest = request;
    const endpoint = new URL(this.dishAutocompleteUrlValue, window.location.origin);
    endpoint.searchParams.set("id", dishId);

    fetch(endpoint.toString(), {
      headers: { Accept: "application/json" },
      signal: request.signal
    })
      .then((response) => (response.ok ? response.json() : []))
      .then((results) => {
        const restoredDish = Array.isArray(results) ? results[0] : null;
        if (!restoredDish || this.selectedExistingDishId() !== dishId) return;

        this.chooseExistingDish(dishId, restoredDish.name, { dispatchChange: false });
      })
      .catch((error) => {
        if (error?.name !== "AbortError") this.showDishSearchMode();
      })
      .finally(() => {
        if (this.currentRestoredDishRequest === request) this.currentRestoredDishRequest = null;
      });
  }

  abortRestoredDishRequest() {
    if (!this.currentRestoredDishRequest) return;

    this.currentRestoredDishRequest.abort();
    this.currentRestoredDishRequest = null;
  }

  clearDishSuggestionTimers() {
    if (this.dishSuggestionFetchTimer) {
      window.clearTimeout(this.dishSuggestionFetchTimer);
      this.dishSuggestionFetchTimer = null;
    }
  }

  normalizedDishName(value) {
    return (value || "").trim().replace(/\s+/g, " ");
  }

  canCreateNewDish(query) {
    return (this.hasAllowNewDishCreationValue ? this.allowNewDishCreationValue : true) &&
      query.length >= NEW_DISH_MIN_QUERY_LENGTH;
  }

  maybeFillPreparationName(value) {
    if (!this.autoFillPreparationNameValue || this.preparationNameTouched || !this.hasPreparationNameFieldTarget) return;

    const normalized = (value || "").trim().replace(/\s+/g, " ");
    this.preparationNameFieldTarget.value = normalized;
  }

  selectedDishName() {
    if (!this.hasDishSelectTarget || this.dishSelectTarget.value === "") return "";

    if (this.dishSelectTarget.tagName !== "SELECT") {
      const fallbackName = this.hasDishSearchInputTarget ? this.dishSearchInputTarget.value : "";
      return this.normalizedDishName(this.dishSelectTarget.dataset.dishName || fallbackName);
    }

    const selectedOption = this.dishSelectTarget.selectedOptions?.[0];
    return (selectedOption?.textContent || "").trim();
  }

  selectedExistingDishId() {
    if (!this.hasDishSelectTarget || this.dishSelectTarget.value === "") return "";

    return this.dishSelectTarget.value;
  }

  clearPreviousPreparationCopyAnnouncement() {
    const announcement = document.querySelector("#preparation-copy-announcement");
    if (announcement) announcement.textContent = "";
  }

  previousPreparationCopyLoadedDishId() {
    if (!this.hasPreviousPreparationCopyPanelTarget) return "";

    return this.previousPreparationCopyPanelTarget.dataset.previousPreparationCopyLoadedDishId || "";
  }

  setPreviousPreparationCopyLoadedDishId(dishId) {
    if (!this.hasPreviousPreparationCopyPanelTarget) return;

    if (dishId) {
      this.previousPreparationCopyPanelTarget.dataset.previousPreparationCopyLoadedDishId = dishId.toString();
    } else {
      delete this.previousPreparationCopyPanelTarget.dataset.previousPreparationCopyLoadedDishId;
    }
  }

  syncPreviousPreparationCopyPanel() {
    if (!this.hasPreviousPreparationCopyPanelTarget) return;
    if (this.previousPreparationCopyPanelTarget.dataset.previousPreparationCopyDeferred === "true") return;

    const dishId = this.selectedExistingDishId();
    if (!dishId) {
      this.clearPreviousPreparationCopyPanel();
      return;
    }

    if (this.previousPreparationCopyLoadedDishId() === dishId && this.previousPreparationCopyPanelTarget.innerHTML.trim() !== "") {
      return;
    }

    this.clearPreviousPreparationCopyPanel();
    this.fetchPreviousPreparationCopyPanel(dishId);
  }

  fetchPreviousPreparationCopyPanel(dishId) {
    if (!dishId || !this.hasPreviousPreparationCopyUrlValue) {
      this.clearPreviousPreparationCopyPanel();
      return;
    }

    this.abortPreviousPreparationCopyRequest();

    const request = new AbortController();
    this.currentPreviousPreparationCopyRequest = request;
    this.previousPreparationCopyRequestedDishId = dishId;

    const endpoint = new URL(this.previousPreparationCopyUrlValue, window.location.origin);
    endpoint.searchParams.set("dish_id", dishId);

    fetch(endpoint.toString(), {
      headers: { Accept: "text/html" },
      signal: request.signal
    })
      .then(response => {
        if (response.status === 204) return "";
        return response.ok ? response.text() : "";
      })
      .then(html => {
        if (this.currentPreviousPreparationCopyRequest !== request) return;
        if (this.previousPreparationCopyRequestedDishId !== dishId) return;
        this.renderPreviousPreparationCopyPanel(html, dishId);
      })
      .catch(error => {
        if (error?.name === "AbortError") return;
        this.clearPreviousPreparationCopyPanel();
      })
      .finally(() => {
        if (this.currentPreviousPreparationCopyRequest === request) {
          this.currentPreviousPreparationCopyRequest = null;
          this.previousPreparationCopyRequestedDishId = null;
        }
      });
  }

  renderPreviousPreparationCopyPanel(html, dishId) {
    if (!this.hasPreviousPreparationCopyPanelTarget) return;

    const content = (html || "").trim();
    if (!content) {
      this.clearPreviousPreparationCopyPanel();
      return;
    }

    this.previousPreparationCopyPanelTarget.innerHTML = content;
    this.previousPreparationCopyPanelTarget.classList.remove("hidden");
    this.setPreviousPreparationCopyLoadedDishId(dishId);
    this.dispatchPreparationEvent("preparation:form-layout-changed");
  }

  clearPreviousPreparationCopyPanel() {
    if (!this.hasPreviousPreparationCopyPanelTarget) return;

    this.abortPreviousPreparationCopyRequest();
    this.previousPreparationCopyPanelTarget.innerHTML = "";
    this.previousPreparationCopyPanelTarget.classList.add("hidden");
    this.setPreviousPreparationCopyLoadedDishId("");
    this.previousPreparationCopyRequestedDishId = null;
  }

  enablePreviousPreparationCopyAfterDishChange() {
    if (!this.hasPreviousPreparationCopyPanelTarget) return;

    delete this.previousPreparationCopyPanelTarget.dataset.previousPreparationCopyDeferred;
  }

  abortPreviousPreparationCopyRequest() {
    if (!this.currentPreviousPreparationCopyRequest) return;

    this.currentPreviousPreparationCopyRequest.abort();
    this.currentPreviousPreparationCopyRequest = null;
  }

  copyPreviousPreparation(event) {
    event.preventDefault();

    if (!this.hasPreviousPreparationCopyApplyUrlValue) return;

    const preparationId = event.currentTarget?.value || "";
    const dishId = this.selectedExistingDishId();
    if (!preparationId || !dishId) return;

    this.abortPreviousPreparationCopyApplyRequest();
    this.clearPreviousPreparationCopyAnnouncement();

    const request = new AbortController();
    this.currentPreviousPreparationCopyApplyRequest = request;
    this.setPreviousPreparationCopyRequestState(true, "Applying the previous preparation…");

    const body = new URLSearchParams({
      previous_preparation_copy_source: preparationId,
      "preparation[dish_id]": dishId
    });

    fetch(this.previousPreparationCopyApplyUrlValue, {
      method: "POST",
      body,
      headers: {
        Accept: "text/vnd.turbo-stream.html",
        "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
        "X-CSRF-Token": this.csrfToken()
      },
      signal: request.signal
    })
      .then(async response => {
        const html = await response.text();
        if (this.currentPreviousPreparationCopyApplyRequest !== request) return;

        if (this.selectedExistingDishId() !== dishId) {
          this.currentPreviousPreparationCopyApplyRequest = null;
          this.setPreviousPreparationCopyRequestState(false);
          return;
        }

        if (!response.ok) {
          this.currentPreviousPreparationCopyApplyRequest = null;
          this.resetPreviousPreparationCopyChoice(preparationId);
          this.setPreviousPreparationCopyRequestState(false, "We couldn’t use that preparation as a starting point. Please try again.");
          return;
        }

        window.Turbo.renderStreamMessage(html);
        this.currentPreviousPreparationCopyApplyRequest = null;
        window.requestAnimationFrame(() => {
          const announcement = document.querySelector("#preparation-copy-announcement");
          if (announcement) announcement.textContent = "Previous preparation applied. Previous details have been filled in.";
          document.querySelector("#preparation_preparation_name")?.focus({ preventScroll: true });
        });
      })
      .catch(error => {
        if (this.currentPreviousPreparationCopyApplyRequest !== request) return;
        if (error?.name === "AbortError") return;

        this.currentPreviousPreparationCopyApplyRequest = null;
        this.resetPreviousPreparationCopyChoice(preparationId);
        this.setPreviousPreparationCopyRequestState(false, "We couldn’t use that preparation as a starting point. Please try again.");
      });
  }

  resetPreviousPreparationCopyChoice(preparationId) {
    if (!this.hasPreviousPreparationCopyChoicesTarget) return;

    const choice = Array.from(this.previousPreparationCopyChoicesTarget.querySelectorAll("input"))
      .find(input => input.value === preparationId.toString());
    if (choice) choice.checked = false;
  }

  setPreviousPreparationCopyRequestState(isBusy, message = "") {
    if (this.hasPreviousPreparationCopyChoicesTarget) {
      this.previousPreparationCopyChoicesTarget.setAttribute("aria-busy", String(isBusy));
      this.previousPreparationCopyChoicesTarget.querySelectorAll("input").forEach(input => {
        input.disabled = isBusy;
      });
    }

    if (!this.hasPreviousPreparationCopyStatusTarget) return;

    this.previousPreparationCopyStatusTarget.textContent = message;
    this.previousPreparationCopyStatusTarget.classList.toggle("hidden", message === "");
    if (message && !isBusy) this.previousPreparationCopyStatusTarget.focus({ preventScroll: true });
  }

  abortPreviousPreparationCopyApplyRequest() {
    if (!this.currentPreviousPreparationCopyApplyRequest) return;

    this.currentPreviousPreparationCopyApplyRequest.abort();
    this.currentPreviousPreparationCopyApplyRequest = null;
    this.setPreviousPreparationCopyRequestState(false);
  }

  csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content || "";
  }

  updateWhatChangedFieldVisibility() {
    if (!this.hasWhatChangedFieldTarget) return;

    const shouldShow = this.shouldShowWhatChangedField();
    this.whatChangedFieldTarget.classList.toggle("ec-field-group--conditional-hidden", !shouldShow);
    this.whatChangedFieldTarget.toggleAttribute("hidden", !shouldShow);
  }

  shouldShowWhatChangedField() {
    if (!this.hasDishSelectTarget) return false;

    const selectedDishId = Number.parseInt(this.dishSelectTarget.value, 10);
    if (!Number.isFinite(selectedDishId)) return false;

    return this.hasHistoryDishIdsValue && this.historyDishIdsValue.includes(selectedDishId);
  }

  validateDishGroup(context = {}) {
    if (!this.hasDishSearchInputTarget) return true;

    const options = context instanceof Event ? { event: context } : context;
    const related = options.event?.relatedTarget;
    const skipFocusBoundaryCheck = options.skipFocusBoundaryCheck === true;
    const force = options.force === true;
    const typedDishName = this.normalizedDishName(this.dishSearchInputTarget.value);

    if (options.event?.type === "focusout" && this.dishSuggestionsDismissedQuery === typedDishName) {
      this.dishSuggestionsDismissedQuery = null;
      return true;
    }

    if (!skipFocusBoundaryCheck && related && this.hasDishGroupFieldsetTarget && this.dishGroupFieldsetTarget.contains(related)) return true;
    if (!this.shouldValidate("dish", { force })) {
      this.clearDishGroupError();
      this.removeSummary("dish");
      return true;
    }

    const hasSelect = this.hasDishSelectTarget && this.dishSelectTarget.value !== "";
    const name = this.hasNewDishFieldTarget ? this.normalizedDishName(this.newDishFieldTarget.value) : "";
    const canConfirmTypedDish = this.hasNewDishFieldTarget && this.canCreateNewDish(typedDishName);
    const inlineMessage = canConfirmTypedDish
      ? `Select an existing dish, or select the Create new dish: “${typedDishName}” option below to use this name.`
      : this.hasNewDishFieldTarget
        ? "Please choose an existing dish or enter a new dish name."
        : "Please choose an existing shared dish or enter a new shared dish name.";
    const summaryMessage = "Dish is required.";

    if (!hasSelect && !name) {
      this.markInvalid(this.dishSearchInputTarget, true);
      this.markInvalid(this.hasNewDishDescriptionTarget ? this.newDishDescriptionTarget : null, false);
      this.showDishGroupError(inlineMessage);
      this.showSummary(summaryMessage, "dish");
      if (canConfirmTypedDish) this.fetchDishSuggestions(typedDishName);
      return false;
    }

    this.markInvalid(this.dishSearchInputTarget, false);
    this.markInvalid(this.hasNewDishDescriptionTarget ? this.newDishDescriptionTarget : null, false);
    this.clearDishGroupError();
    this.removeSummary("dish");
    return true;
  }

  validateDateCooked(context = {}) {
    const options = context instanceof Event ? { event: context } : context;
    const force = options.force === true;
    const val = (this.dateCookedTarget?.value || "").trim();
    const inlineMessage = "Please select the date you cooked this.";
    const summaryMessage = "Date cooked is required.";

    if (!this.shouldValidate("date_cooked", { force })) {
      this.clearDateCookedError();
      this.removeSummary("date_cooked");
      return true;
    }

    if (!val) {
      this.markInvalid(this.dateCookedTarget, true);
      if (this.hasDateCookedErrorTarget) {
        this.dateCookedErrorTarget.textContent = inlineMessage;
        this.dateCookedErrorTarget.classList.remove("hidden");
      }
      this.showSummary(summaryMessage, "date_cooked");
      return false;
    }

    this.clearDateCookedError();
    this.removeSummary("date_cooked");
    return true;
  }

  validatePreparationName(context = {}) {
    const options = context instanceof Event ? { event: context } : context;
    const force = options.force === true;
    const value = (this.preparationNameFieldTarget?.value || "").trim();
    const inlineMessage = "Please enter a preparation name.";
    const summaryMessage = "Preparation name is required.";

    if (!this.shouldValidate("preparation_name", { force })) {
      this.clearPreparationNameError();
      this.removeSummary("preparation_name");
      return true;
    }

    if (!value) {
      this.markInvalid(this.preparationNameFieldTarget, true);
      if (this.hasPreparationNameErrorTarget) {
        this.preparationNameErrorTarget.textContent = inlineMessage;
        this.preparationNameErrorTarget.classList.remove("hidden");
      }
      this.showSummary(summaryMessage, "preparation_name");
      return false;
    }

    this.clearPreparationNameError();
    this.removeSummary("preparation_name");
    return true;
  }

  validateOccasionGroup(context = {}) {
    if (!this.hasNewOccasionCheckboxTarget || !this.hasOccasionTypeSelectTarget) return true;

    const options = context instanceof Event ? { event: context } : context;
    const related = options.event?.relatedTarget;
    const skipFocusBoundaryCheck = options.skipFocusBoundaryCheck === true;
    const force = options.force === true;

    if (!skipFocusBoundaryCheck && related && this.hasOccasionGroupFieldsetTarget && this.occasionGroupFieldsetTarget.contains(related)) return true;

    const checkboxChecked = this.newOccasionCheckboxTarget.checked;
    const fields = this.occasionFields();
    const {
      nameField,
      typeSelect,
      monthField,
      dayField,
      dateField
    } = fields;

    const name = (nameField?.value || "").trim();
    const selectedType = typeSelect?.value || "";
    const month = monthField?.value || "";
    const day = dayField?.value || "";
    const date = dateField?.value || "";
    const hasRecurringBits = Boolean(month || day);
    const hasOneTimeDate = Boolean(date);
    const hasConflict = (selectedType === "recurring" && hasOneTimeDate) ||
      (selectedType === "one_time" && hasRecurringBits);

    this.clearOccasionGroupError();
    this.removeSummary("occasion");

    if (!checkboxChecked) return true;
    if (!this.shouldValidate("occasion", { force })) return true;

    const postSubmitMode = this.hasAttemptedSubmit || force;
    const shouldRequireName = postSubmitMode;
    const shouldRequireRecurring = selectedType === "recurring" && postSubmitMode;
    const shouldRequireOneTime = selectedType === "one_time" && postSubmitMode;

    let errorFound = false;
    let nameMissing = false;
    let monthMissing = false;
    let dayMissing = false;
    let dateMissing = false;

    if (shouldRequireName && !name) {
      this.markInvalid(nameField, true);
      nameMissing = true;
      errorFound = true;
    } else {
      this.markInvalid(nameField, false);
    }

    if (selectedType === "recurring") {
      if (shouldRequireRecurring && !month) {
        this.markInvalid(monthField, true);
        monthMissing = true;
        errorFound = true;
      } else {
        this.markInvalid(monthField, false);
      }

      if (shouldRequireRecurring && !day) {
        this.markInvalid(dayField, true);
        dayMissing = true;
        errorFound = true;
      } else {
        this.markInvalid(dayField, false);
      }

      this.markInvalid(dateField, false);
    } else if (selectedType === "one_time") {
      if (shouldRequireOneTime && !date) {
        this.markInvalid(dateField, true);
        dateMissing = true;
        errorFound = true;
      } else {
        this.markInvalid(dateField, false);
      }

      this.markInvalid(monthField, false);
      this.markInvalid(dayField, false);
    } else {
      this.markInvalid(monthField, false);
      this.markInvalid(dayField, false);
      this.markInvalid(dateField, false);
    }

    if (hasConflict) {
      this.markInvalid(dateField, true);
      this.markInvalid(monthField, true);
      this.markInvalid(dayField, true);
      errorFound = true;
    }

    if (!errorFound) return true;

    const { inlineMessage, summaryMessage } = this.occasionValidationMessages({
      selectedType,
      hasConflict,
      nameMissing,
      monthMissing,
      dayMissing,
      dateMissing
    });

    this.showOccasionGroupError(inlineMessage);
    if (this.hasAttemptedSubmit || force) {
      this.showSummary(summaryMessage, "occasion");
    }

    return false;
  }

  occasionFields() {
    return {
      nameField: this.element.querySelector('[name="preparation[occasions_attributes][0][name]"]'),
      typeSelect: this.hasOccasionTypeSelectTarget ? this.occasionTypeSelectTarget : null,
      monthField: this.element.querySelector('[name="preparation[occasions_attributes][0][month]"]:not([data-occasion-timing-target~="clearField"])'),
      dayField: this.element.querySelector('[name="preparation[occasions_attributes][0][day]"]:not([data-occasion-timing-target~="clearField"])'),
      dateField: this.element.querySelector('[name="preparation[occasions_attributes][0][date]"]:not([data-occasion-timing-target~="clearField"])')
    };
  }

  occasionValidationMessages(flags = {}) {
    if (flags.hasConflict) {
      return {
        inlineMessage: "An occasion cannot be both one-time and recurring. Choose one type.",
        summaryMessage: "Choose one occasion type."
      };
    }

    if (flags.selectedType === "recurring") {
      if (flags.nameMissing && (flags.monthMissing || flags.dayMissing)) {
        return {
          inlineMessage: "Please enter an occasion name, and select a month and day for recurrence.",
          summaryMessage: "Occasion details are incomplete."
        };
      }

      if (flags.monthMissing || flags.dayMissing) {
        return {
          inlineMessage: "Please select both a month and day for recurrence.",
          summaryMessage: "Occasion details are incomplete."
        };
      }
    }

    if (flags.selectedType === "one_time") {
      if (flags.nameMissing && flags.dateMissing) {
        return {
          inlineMessage: "Please enter an occasion name and select a date.",
          summaryMessage: "Occasion details are incomplete."
        };
      }

      if (flags.dateMissing) {
        return {
          inlineMessage: "Please select a date for this occasion.",
          summaryMessage: "Occasion details are incomplete."
        };
      }
    }

    return {
      inlineMessage: "Please enter an occasion name.",
      summaryMessage: "Occasion details are incomplete."
    };
  }

  showOccasionGroupError(message) {
    if (!this.hasOccasionGroupErrorTarget) return;

    this.occasionGroupErrorTarget.textContent = message;
    this.occasionGroupErrorTarget.classList.remove("hidden");
  }

  clearOccasionGroupError() {
    if (this.hasOccasionGroupErrorTarget) {
      this.occasionGroupErrorTarget.textContent = "";
      this.occasionGroupErrorTarget.classList.add("hidden");
    }

    const { nameField, monthField, dayField, dateField } = this.occasionFields();
    [ nameField, monthField, dayField, dateField ].forEach((field) => {
      if (field) this.markInvalid(field, false);
    });
  }

  validate(event) {
    this.hasAttemptedSubmit = true;
    this.dishSuggestionsDismissedQuery = null;
    this.clearErrors();

    const validity = [
      this.validateDishGroup({ force: true, skipFocusBoundaryCheck: true }),
      this.validateOccasionGroup({ force: true, skipFocusBoundaryCheck: true }),
      this.validatePreparationName({ force: true }),
      this.validateDateCooked({ force: true })
    ];
    const hasErrors = validity.some((result) => result === false);

    if (hasErrors) {
      event.preventDefault();
      if (!this.focusFirstInvalidField()) {
        const notice = this.usingNoticeMode() ? this.noticeElement() : this.errorBoxTarget;
        notice?.focus();
      }
    }
  }

  errorSummaryMode() {
    return this.hasErrorSummaryModeValue ? this.errorSummaryModeValue : "summary";
  }

  usingNoticeMode() {
    return this.errorSummaryMode() === "notice";
  }

  noticeMessage() {
    return this.hasNoticeMessageValue ? this.noticeMessageValue : "Please review the errors below.";
  }

  noticeElement() {
    const existing = this.element.querySelector("[data-client-validation-notice]");
    if (existing) {
      const clientNotice = this.hasErrorBoxTarget ? this.errorBoxTarget : null;
      if (existing !== clientNotice) this.serverRenderedNoticeElement = existing;
      return existing;
    }

    if (!this.hasErrorBoxTarget) return null;

    this.errorBoxTarget.dataset.clientValidationNotice = "true";
    return this.errorBoxTarget;
  }

  hasValidationErrors() {
    return Boolean(this.element.querySelector("[aria-invalid='true']:not([disabled])"));
  }

  showSummary(message, type, options = {}) {
    if (this.usingNoticeMode()) {
      this.updateErrorSummaryVisibility();
      return;
    }

    if (!this.hasErrorBoxTarget) return;

    const list = this.errorBoxTarget.querySelector("ul");
    if (!list) return;

    const localizable = options.localizable !== false;
    let li = this.errorBoxTarget.querySelector(`li[data-error-type="${type}"]`);

    if (!li) {
      li = document.createElement("li");
      li.setAttribute("data-error-type", type);
      list.appendChild(li);
    }

    li.setAttribute("data-error-localizable", localizable ? "true" : "false");

    const errorField = this.errorFieldForType(type);
    if (errorField?.id) {
      const link = document.createElement("a");
      link.href = `#${errorField.id}`;
      link.textContent = message;
      link.className = "ec-form-error-summary-link";
      link.addEventListener("click", (event) => {
        event.preventDefault();
        errorField.focus();
      });
      li.replaceChildren(link);
    } else {
      li.textContent = message;
    }

    this.updateErrorSummaryVisibility();
  }

  removeSummary(type) {
    if (this.usingNoticeMode()) {
      this.updateErrorSummaryVisibility();
      return;
    }

    if (!this.hasErrorBoxTarget) return;

    const li = this.errorBoxTarget.querySelector(`li[data-error-type="${type}"]`);
    if (li) li.remove();
    this.updateErrorSummaryVisibility();
  }

  markInvalid(el, invalid) {
    if (!el) return;
    el.classList.toggle("ec-field-control--invalid", invalid);
    el.setAttribute("aria-invalid", invalid);
  }

  focusFirstInvalidField() {
    if (!this.hasFormTarget) return false;

    const invalidField = this.formTarget.querySelector("[aria-invalid='true']:not([disabled])");
    if (!invalidField) return false;

    invalidField.focus();
    return true;
  }

  errorFieldForType(type) {
    if (type === "dish") return this.hasDishSearchInputTarget ? this.dishSearchInputTarget : this.newDishFieldTarget;
    if (type === "date_cooked") return this.dateCookedTarget;
    if (type === "preparation_name") return this.preparationNameFieldTarget;
    if (type === "occasion") {
      return this.element.querySelector('[name="preparation[occasions_attributes][0][name]"]') ||
        (this.hasNewOccasionCheckboxTarget ? this.newOccasionCheckboxTarget : null);
    }

    return null;
  }

  showDishGroupError(message) {
    if (!this.hasDishGroupErrorTarget) return;

    this.dishGroupErrorTarget.textContent = message;
    this.dishGroupErrorTarget.classList.remove("hidden");
  }

  clearDishGroupError() {
    if (this.hasDishGroupErrorTarget) {
      this.dishGroupErrorTarget.textContent = "";
      this.dishGroupErrorTarget.classList.add("hidden");
    }

    const maybeDescriptionField = this.hasNewDishDescriptionTarget ? this.newDishDescriptionTarget : null;
    [ this.dishSearchInputTarget, maybeDescriptionField ].forEach((element) =>
      this.markInvalid(element, false)
    );
  }

  clearDateCookedError() {
    this.markInvalid(this.dateCookedTarget, false);
    if (this.hasDateCookedErrorTarget) {
      this.dateCookedErrorTarget.textContent = "";
      this.dateCookedErrorTarget.classList.add("hidden");
    }
  }

  clearPreparationNameError() {
    this.markInvalid(this.preparationNameFieldTarget, false);
    if (this.hasPreparationNameErrorTarget) {
      this.preparationNameErrorTarget.textContent = "";
      this.preparationNameErrorTarget.classList.add("hidden");
    }
  }

  clearErrors() {
    if (this.usingNoticeMode()) {
      const notice = this.noticeElement();
      if (!notice) return;

      notice.classList.add("hidden");
      notice.textContent = "";
      return;
    }

    if (!this.hasErrorBoxTarget) return;

    this.errorBoxTarget.classList.add("hidden");

    const list = this.errorBoxTarget.querySelector("ul");
    if (list) list.innerHTML = "";
  }

  updateErrorSummaryVisibility() {
    if (this.usingNoticeMode()) {
      const notice = this.noticeElement();
      if (!notice) return;

      if (!this.hasAttemptedSubmit && this.serverRenderedNoticeElement?.isConnected) {
        return;
      }

      if (!this.hasValidationErrors() && !this.hasVisibleFieldErrors()) {
        notice.textContent = "";
        notice.classList.add("hidden");
        return;
      }

      notice.textContent = this.noticeMessage();
      notice.classList.remove("hidden");
      return;
    }

    if (!this.hasErrorBoxTarget) return;

    if (!this.hasAttemptedSubmit) {
      this.errorBoxTarget.classList.add("hidden");
      return;
    }

    const entries = Array.from(this.errorBoxTarget.querySelectorAll("li"));
    if (entries.length === 0) {
      this.errorBoxTarget.classList.add("hidden");
      return;
    }

    if (entries.length === 1 && entries[0].dataset.errorLocalizable === "true") {
      this.errorBoxTarget.classList.add("hidden");
      return;
    }

    this.errorBoxTarget.classList.remove("hidden");
  }

  hasVisibleFieldErrors() {
    return Boolean(this.element.querySelector(".ec-field-error:not(.hidden)"));
  }

  toggleNewOccasion(event) {
    const show = event.target.checked;
    this.newOccasionFieldsTarget.style.display = show ? "block" : "none";
    this.markValidationTouched("occasion");
    if (!show) {
      this.clearOccasionGroupError();
      this.removeSummary("occasion");
      this.resetOccasionInteractionState();
    }
    this.updateOccasionFieldRequirements();
  }

  occasionTypeChanged() {
    this.markValidationTouched("occasion");
    this.clearOccasionGroupError();
    this.removeSummary("occasion");
    this.updateOccasionFieldRequirements();
  }

  updateOccasionFieldRequirements() {
    const creatingOccasion = this.hasNewOccasionCheckboxTarget && this.newOccasionCheckboxTarget.checked;
    const selectedType = this.hasOccasionTypeSelectTarget ? this.occasionTypeSelectTarget.value : "";

    const { nameField, monthField, dayField, dateField } = this.occasionFields();
    const visibilityFields = this.element.querySelectorAll('[name="preparation[occasions_attributes][0][public]"]');

    if (this.hasOccasionTypeSelectTarget) {
      this.occasionTypeSelectTarget.disabled = !creatingOccasion;
    }

    if (nameField) {
      nameField.required = creatingOccasion;
      nameField.disabled = !creatingOccasion;
    }

    const recurringRequired = creatingOccasion && selectedType === "recurring";
    if (monthField) {
      monthField.required = recurringRequired;
      monthField.disabled = !recurringRequired;
    }
    if (dayField) {
      dayField.required = recurringRequired;
      dayField.disabled = !recurringRequired;
    }

    const oneTimeRequired = creatingOccasion && selectedType === "one_time";
    if (dateField) {
      dateField.required = oneTimeRequired;
      dateField.disabled = !oneTimeRequired;
    }

    visibilityFields.forEach((field) => {
      field.disabled = !creatingOccasion;
    });
  }

  resetOccasionInteractionState() {
    this.touchedValidationKeys.delete("occasion");
    this.dirtyValidationKeys.delete("occasion");

    const checkbox = this.hasNewOccasionCheckboxTarget ? this.newOccasionCheckboxTarget : null;
    const fields = this.occasionFields();
    [ checkbox, fields.typeSelect, fields.nameField, fields.monthField, fields.dayField, fields.dateField ].forEach((field) => {
      const fieldKey = this.validationFieldKey(field);
      if (!fieldKey) return;

      this.touchedValidationFields.delete(fieldKey);
      this.dirtyValidationFields.delete(fieldKey);
    });
  }

  addIngredient(event) {
    event.preventDefault();
    if (!this.hasIngredientsContainerTarget || !this.hasIngredientTemplateTarget) return;

    this.appendRecipeField(this.ingredientsContainerTarget, this.ingredientTemplateTarget, "input[type='text']");
  }

  addStep(event) {
    event.preventDefault();
    if (!this.hasStepsContainerTarget || !this.hasStepTemplateTarget) return;

    this.appendRecipeField(this.stepsContainerTarget, this.stepTemplateTarget, "textarea");
  }

  addRecipeSection(event) {
    event.preventDefault();
    if (!this.hasRecipeSectionsContainerTarget || !this.hasRecipeSectionTemplateTarget) return;

    const nextSectionIndex = this.nextRecipeSectionIndex();
    const nextSectionPosition = this.nextRecipeSectionPosition();
    const sectionHtml = this.recipeSectionTemplateTarget.innerHTML
      .replaceAll("__SECTION_INDEX__", String(nextSectionIndex))
      .replaceAll("__SECTION_POSITION__", String(nextSectionPosition));

    this.recipeSectionsContainerTarget.insertAdjacentHTML("beforeend", sectionHtml);
    this.recipeSectionsContainerTarget.dataset.nextSectionIndex = String(nextSectionIndex + 1);
    this.recipeSectionsContainerTarget.dataset.nextSectionPosition = String(nextSectionPosition + 1);

    const newSection = this.recipeSectionsContainerTarget.lastElementChild;
    const titleField = newSection?.querySelector("input[id$='_title']");
    if (titleField) titleField.focus();

    window.requestAnimationFrame(() => {
      this.refreshSortableLists(newSection);
    });
  }

  addRecipeSectionIngredient(event) {
    event.preventDefault();

    const section = this.recipeSectionElementForAction(event);
    if (!section) return;

    const container = section.querySelector("[data-recipe-section-ingredients-container]");
    const template = section.querySelector("[data-recipe-section-ingredient-template]");
    if (!container || !template) return;

    this.appendRecipeField(container, template, "input[type='text']");
  }

  addRecipeSectionStep(event) {
    event.preventDefault();

    const section = this.recipeSectionElementForAction(event);
    if (!section) return;

    const container = section.querySelector("[data-recipe-section-steps-container]");
    const template = section.querySelector("[data-recipe-section-step-template]");
    if (!container || !template) return;

    this.appendRecipeField(container, template, "textarea");
  }

  removeRecipeSection(event) {
    event.preventDefault();

    const section = this.recipeSectionElementForAction(event);
    if (!section) return;

    const destroyField = section.querySelector("[data-recipe-section-destroy]");
    if (destroyField) {
      destroyField.value = "1";
      destroyField.disabled = false;
    }

    section.querySelectorAll("input, textarea, select").forEach((field) => {
      if (field === destroyField) return;
      field.disabled = true;
    });
    section.classList.add("hidden");
  }

  appendRecipeField(container, template, focusSelector) {
    const nextIndex = this.nextRecipeIndex(container);
    const nextPosition = this.nextRecipePosition(container);
    const rowHtml = template.innerHTML
      .replaceAll("__INDEX__", String(nextIndex))
      .replaceAll("__POSITION__", String(nextPosition));

    container.insertAdjacentHTML("beforeend", rowHtml);
    container.dataset.nextIndex = String(nextIndex + 1);

    const newField = container.lastElementChild?.querySelector(focusSelector);
    if (newField) {
      newField.focus();
    }

    this.refreshSortableList(container);
  }

  nextRecipeIndex(container) {
    const parsed = Number.parseInt(container.dataset.nextIndex || "", 10);
    return Number.isNaN(parsed) ? container.children.length : parsed;
  }

  nextRecipePosition(container) {
    const positions = Array.from(container.querySelectorAll("[data-recipe-position-field]"))
      .filter((field) => !this.isMarkedForDestroy(field))
      .map((field) => Number.parseInt(field.value, 10))
      .filter((value) => !Number.isNaN(value));

    return (positions.length > 0 ? Math.max(...positions) : 0) + 1;
  }

  nextRecipeSectionIndex() {
    const parsed = Number.parseInt(this.recipeSectionsContainerTarget.dataset.nextSectionIndex || "", 10);
    return Number.isNaN(parsed) ? this.recipeSectionsContainerTarget.children.length : parsed;
  }

  nextRecipeSectionPosition() {
    const parsed = Number.parseInt(this.recipeSectionsContainerTarget.dataset.nextSectionPosition || "", 10);
    if (!Number.isNaN(parsed)) return parsed;

    const positions = Array.from(this.recipeSectionsContainerTarget.querySelectorAll("[data-recipe-section-position-field]"))
      .map((field) => Number.parseInt(field.value, 10))
      .filter((value) => !Number.isNaN(value));

    return (positions.length > 0 ? Math.max(...positions) : 0) + 1;
  }

  recipeSectionElementForAction(event) {
    return event.currentTarget.closest("[data-recipe-section]");
  }

  refreshSortableLists(rootElement = this.element) {
    if (!rootElement) return;

    rootElement.querySelectorAll("[data-controller~='sortable-list']").forEach((sortableElement) => {
      this.refreshSortableList(sortableElement);
    });
  }

  refreshSortableList(element) {
    if (!element) return;

    const controller = this.application.getControllerForElementAndIdentifier(element, "sortable-list");
    controller?.refresh();
  }

  refreshSortableListForField(field) {
    const sortableElement = field?.closest?.("[data-controller~='sortable-list']");
    if (!sortableElement) return;

    this.refreshSortableList(sortableElement);
  }

  isMarkedForDestroy(field) {
    const row = field.closest("[data-sortable-list-target='item'], [data-recipe-section]");
    if (!row) return false;

    const destroyCheckbox = row.querySelector("input[type='checkbox'][name$='[_destroy]']");
    return destroyCheckbox?.checked === true;
  }

  clearTravelFields() {
    const fieldNames = [
      "travel_country",
      "travel_region",
      "travel_city",
      "travel_year",
      "travel_memory_note"
    ];

    fieldNames.forEach((fieldName) => {
      const field = this.element.querySelector(`[name="preparation[${fieldName}]"]`);
      if (field) field.value = "";
    });
  }
}
